import { Extension, type Editor } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';

function atStartOfNode(state: EditorState) {
  const { $from } = state.selection;
  return $from.parentOffset === 0; // start of current node content
}

function atEndOfNode(state: EditorState) {
  const { $from } = state.selection;
  return $from.parentOffset === $from.parent.content.size; // end of current node content
}

/**
 * Extension for Code Editor to avoid that users can "break out" of the editor
 * by using arrow keys and mix the code block with regular text within the
 * TLightEditor.
 */
export const trapCodeBlockArrows = Extension.create({
  name: 'trapCodeBlockArrows',
  addKeyboardShortcuts() {
    const handleAtBoundary = (
      editor: Editor,
      state: EditorState,
      boundaryCheck: (s: EditorState) => boolean,
    ) => {
      // only act when caret is inside a code block and selection is a caret
      if (!editor.isActive('codeBlock')) return false;
      if (!state.selection.empty) return false;
      return boundaryCheck(state); // true = handled, prevent default
    };
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ArrowUp: (args) =>
        handleAtBoundary(args.editor, args.editor.state, atStartOfNode),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ArrowLeft: (args) =>
        handleAtBoundary(args.editor, args.editor.state, atStartOfNode),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ArrowDown: (args) =>
        handleAtBoundary(args.editor, args.editor.state, atEndOfNode),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ArrowRight: (args) =>
        handleAtBoundary(args.editor, args.editor.state, atEndOfNode),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Enter: ({ editor }) => {
        // Only handle Enter when inside a code block
        if (!editor.isActive('codeBlock')) return false;
        // Insert a line break within the code block instead of creating a new paragraph
        return editor.commands.insertContent('\n');
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Backspace: ({ editor }) => {
        if (!editor.isActive('codeBlock')) return false;
        const { state } = editor;
        return atStartOfNode(state);
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Delete: ({ editor }) => {
        if (!editor.isActive('codeBlock')) return false;
        const { state } = editor;
        return atEndOfNode(state);
      },
    };
  },
});
